import type { ReactNode } from "react";
import { Badge, IconButton } from "@panelgenie/shared-ui";
import type { TestCaseSummary } from "../types.js";
import { EyeIcon } from "./icons.js";
import { formatInstant, kindLabel, outcomeBadgeVariant, outcomeLabel } from "../lib/format.js";

/**
 * The corpus list table — one row per `support.listTestCases` summary.
 * Mirrors the invocation list table: a stamp + a few projected columns,
 * with the heavy baseline reads deferred to the detail fetch. Each row
 * links to its detail page by id. The "Last run" column resolves the most
 * recent appended run to the outcome of the invocation it produced (or
 * "Processing" when the run hasn't recorded an invocation yet, and "—"
 * when the case has never been run).
 */

export type TestCasesListProps = {
  readonly items: readonly TestCaseSummary[];
  readonly onSelect: (id: string) => void;
};

export function TestCasesList({ items, onSelect }: TestCasesListProps) {
  if (items.length === 0) {
    return (
      <p aria-label="test-cases-empty" className="text-base text-mute">
        No test cases yet. Save one from an invocation's detail view.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-line">
      <table aria-label="test-cases-table" className="w-full text-left text-sm">
        <thead className="border-b border-line bg-subtle text-mute">
          <tr>
            <Th>Name</Th>
            <Th>Kind</Th>
            <Th>Runs</Th>
            <Th>Last run</Th>
            <Th>Created</Th>
            <Th>
              <span className="sr-only">Actions</span>
            </Th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.id} className="border-b border-line last:border-b-0">
              <Td className="max-w-72 truncate font-medium text-fg">{item.name}</Td>
              <Td className="whitespace-nowrap text-mute">{kindLabel(item.kind)}</Td>
              <Td className="text-mute">{item.runCount}</Td>
              <Td>
                <LastRunCell lastRun={item.lastRun} />
              </Td>
              <Td className="whitespace-nowrap text-mute">
                {formatInstant(item.createdAt)}
              </Td>
              <Td>
                <IconButton
                  variant="ghost"
                  size="sm"
                  icon={<EyeIcon />}
                  label={`View test case ${item.id}`}
                  title="View"
                  onClick={() => onSelect(item.id)}
                />
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LastRunCell({ lastRun }: { readonly lastRun: TestCaseSummary["lastRun"] }) {
  if (lastRun === null) {
    return <span className="text-mute">—</span>;
  }
  // A run with no resolved invocation is still in flight (its produced
  // invocation row hasn't landed yet) — surface it as Processing.
  const outcome = lastRun.invocation?.outcome ?? "processing";
  return <Badge variant={outcomeBadgeVariant(outcome)}>{outcomeLabel(outcome)}</Badge>;
}

function Th({ children }: { readonly children: ReactNode }) {
  return <th className="px-3 py-2 font-medium">{children}</th>;
}

function Td({
  children,
  className,
}: {
  readonly children: ReactNode;
  readonly className?: string;
}) {
  return <td className={`px-3 py-2 align-middle ${className ?? ""}`}>{children}</td>;
}
